import { resolveFieldLabel } from "@repo/shared";
import type {
  FinancialEntryItem,
  FinancialFieldDefinition,
  FinancialSituationAccountEntry,
} from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { AddISINModal } from "../add-isin-modal";
import { T } from "./assets-tab.i18n";
import { RequiredTypeLabel } from "./required-type-label";
import { getFieldLabel } from "./translation-utils";
import { TypeSummary } from "./type-summary";
import { formatNumberInput, MAX_ITEMS_PER_CATEGORY, parseNumberInput } from "./utils";

type AssetsTabProps = {
  entries: FinancialEntryItem[];
  fieldDefs: FinancialFieldDefinition[];
  /** QL-39 — ids of the required fields; their label gets a * and their last row cannot be removed. */
  requiredTypeIds?: ReadonlySet<string>;
  /** QL-39 — required fields still lacking an amount; their amount input is outlined. */
  missingRequiredIds?: ReadonlySet<string>;
  disabled: boolean;
  currency: string;
  lang: string;
  totalAssets: number;
  /** QL-40 — server-snapshotted account values, shown read-only in the summary block. */
  accountRows?: FinancialSituationAccountEntry[];
  accountsSyncedAt?: string | null;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<FinancialEntryItem>) => void;
  onAddISINEntries: (entries: FinancialEntryItem[]) => void;
};

export function AssetsTab({
  entries,
  fieldDefs,
  requiredTypeIds,
  missingRequiredIds,
  disabled,
  currency,
  lang,
  totalAssets,
  accountRows = [],
  accountsSyncedAt = null,
  onAdd,
  onRemove,
  onUpdate,
  onAddISINEntries,
}: AssetsTabProps) {
  const { formatNumber, parseNumber } = useRegionalFormat();
  const t = useT(T);
  const isRequired = (typeId: string) => requiredTypeIds?.has(typeId) === true;
  const isMissing = (typeId: string) => missingRequiredIds?.has(typeId) === true;
  const isLastRowOfRequired = (entry: FinancialEntryItem) =>
    isRequired(entry.typeId) && entries.filter((e) => e.typeId === entry.typeId).length === 1;
  const [showISINModal, setShowISINModal] = useState(false);

  // QL-586 — the model's current label, not the one stored when the row was created.
  const labelFor = (entry: FinancialEntryItem) => {
    const def = fieldDefs.find((d) => d.id === entry.typeId);
    return (def ? resolveFieldLabel(def, lang) : "") || entry.typeLabel;
  };

  const summaryByType = useMemo(() => {
    const grouped: Record<string, { label: string; total: number }> = {};
    for (const entry of entries) {
      if (!grouped[entry.typeId]) {
        grouped[entry.typeId] = { label: labelFor(entry), total: 0 };
      }
      grouped[entry.typeId].total += entry.amount || 0;
    }
    return grouped;
  }, [entries, fieldDefs, lang]);

  return (
    <div className="space-y-4">
      {/* Entry list */}
      <div className="space-y-3">
        {entries.length > 0 && (
          <div className="grid gap-2 text-sm font-medium text-muted-foreground grid-cols-[minmax(80px,180px)_minmax(100px,300px)_minmax(80px,165px)_minmax(100px,225px)_32px]">
            <div>{t.isin}</div>
            <div>{t.description}</div>
            <div>{t.amount}</div>
            <div>{t.type}</div>
            <div />
          </div>
        )}

        {entries.map((entry) => (
          <div
            key={entry.id}
            className="grid gap-2 items-center grid-cols-[minmax(80px,180px)_minmax(100px,300px)_minmax(80px,165px)_minmax(100px,225px)_32px]"
          >
            <Input
              value={entry.isin || ""}
              onChange={(e) => onUpdate(entry.id, { isin: e.target.value.toUpperCase() })}
              disabled={disabled}
              placeholder="ISIN..."
              className="h-8 font-mono text-xs"
            />

            <Input
              value={entry.label}
              onChange={(e) => onUpdate(entry.id, { label: e.target.value })}
              disabled={disabled}
              placeholder={t.descriptionPlaceholder}
              className="h-8"
            />

            <Input
              type="text"
              inputMode="numeric"
              value={formatNumberInput(entry.amount, formatNumber)}
              onChange={(e) =>
                onUpdate(entry.id, { amount: parseNumberInput(e.target.value, parseNumber) })
              }
              disabled={disabled}
              placeholder={t.amountPlaceholder}
              aria-invalid={isMissing(entry.typeId) || undefined}
              className={cn("h-8 text-right", isMissing(entry.typeId) && "border-destructive")}
            />

            <Select
              value={entry.typeId}
              onValueChange={(v) => onUpdate(entry.id, { typeId: v })}
              disabled={disabled}
            >
              <SelectTrigger className="h-8">
                <SelectValue>
                  <RequiredTypeLabel
                    label={getFieldLabel(entry.typeId, lang, fieldDefs, t.fieldLabels)}
                    required={isRequired(entry.typeId)}
                  />
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {fieldDefs.map((field) => (
                  <SelectItem key={field.id} value={field.id}>
                    {getFieldLabel(field.id, lang, fieldDefs, t.fieldLabels)}
                    {isRequired(field.id) && <span className="text-destructive"> *</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              tooltip={t.remove}
              onClick={() => onRemove(entry.id)}
              disabled={disabled || isLastRowOfRequired(entry)}
              className="h-8 w-8"
            >
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
        ))}

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAdd}
            disabled={
              disabled || entries.length >= MAX_ITEMS_PER_CATEGORY || fieldDefs.length === 0
            }
            className="flex-1"
          >
            {t.addAsset}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowISINModal(true)}
            disabled={
              disabled || entries.length >= MAX_ITEMS_PER_CATEGORY || fieldDefs.length === 0
            }
            className="flex-1"
          >
            {t.addIsin}
          </Button>
        </div>

        {entries.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">{t.noItemsYetButtons}</p>
        )}
      </div>

      <TypeSummary
        summary={summaryByType}
        total={totalAssets}
        currency={currency}
        lang={lang}
        accountRows={accountRows}
        accountsSyncedAt={accountsSyncedAt}
      />

      {/* ISIN Modal */}
      <AddISINModal
        open={showISINModal}
        onClose={() => setShowISINModal(false)}
        onAdd={onAddISINEntries}
        assetFields={fieldDefs}
        capacity={MAX_ITEMS_PER_CATEGORY - entries.length}
        disabled={disabled}
      />
    </div>
  );
}
